import { useAlert } from '@dwelvio/react-alert';
import { useMutation } from '@apollo/client/react';

import {
  ContractFragment,
  DeleteContractDocument,
  SignContractDocument,
  TerminateContractDocument,
  UnsignContractDocument,
  DeleteContractMutation,
  DeleteContractMutationVariables,
  SignContractMutation,
  SignContractMutationVariables,
  TerminateContractMutation,
  TerminateContractMutationVariables,
  UnsignContractMutation,
  UnsignContractMutationVariables,
} from '../../../generated/graphql';
import { Button, Stack } from '@mui/material';
import { ConfirmContractDialog } from './confirm-contract-dialog';
import React from 'react';

interface ContractDetailMenuProps {
  contract: ContractFragment;
  disabled?: boolean;
}

export function ContractDetailMenu(props: ContractDetailMenuProps) {
  const { contract: c, disabled } = props;
  const { show } = useAlert();
  const [showSignContractDialog, setShowSignContractDialog] = React.useState(false);

  const [signContract, { loading: signLoading }] = useMutation<SignContractMutation, SignContractMutationVariables>(
    SignContractDocument,
    {
      onError: (e: Error) => show.error('Nepodarilo sa označiť prenájom ako potvrdený.'),
    },
  );

  const [unsignContract, { loading: unsignLoading }] = useMutation<
    UnsignContractMutation,
    UnsignContractMutationVariables
  >(UnsignContractDocument, {
    onError: (e: Error) => show.error('Nepodarilo sa zrušiť potvrdenie prenájmu.'),
  });

  const [terminateContract, { loading: terminateLoading }] = useMutation<
    TerminateContractMutation,
    TerminateContractMutationVariables
  >(TerminateContractDocument, {
    onError: (e: Error) => show.error('Nepodarilo sa označiť prenájom ako ukončený.'),
  });

  const [deleteContract, { loading: deleteLoading }] = useMutation<
    DeleteContractMutation,
    DeleteContractMutationVariables
  >(DeleteContractDocument, {
    onError: (e: Error) => show.error('Nepodarilo sa zmazať zmluvu.', e.message),
  });

  const contractId = c.id;

  return (
    <div>
      <Stack spacing={1}>
        {!c.signingDate && !c.deletedAt && (
          <Button variant="outlined" onClick={() => setShowSignContractDialog(true)} disabled={signLoading || disabled}>
            Potvrdiť podpis prenájmu
          </Button>
        )}
        {!!c.signingDate && (
          <Button
            onClick={() => unsignContract({ variables: { contractId } })}
            disabled={unsignLoading || disabled}
            variant="outlined"
            fullWidth
          >
            Zrušiť podpis prenájmu
          </Button>
        )}
        {!!c.signingDate && !c.terminationDate && !c.deletedAt && (
          <Button
            onClick={() => terminateContract({ variables: { contractId, terminationDate: new Date().toISOString() } })}
            disabled={terminateLoading || disabled}
            variant="outlined"
            fullWidth
          >
            Ukončiť prenájom
          </Button>
        )}
        {!c.deletedAt && (
          <Button
            onClick={() => deleteContract({ variables: { contractId } })}
            disabled={deleteLoading || disabled}
            variant="outlined"
            fullWidth
            color="error"
          >
            Zmazať
          </Button>
        )}
      </Stack>

      {showSignContractDialog && (
        <ConfirmContractDialog
          open={showSignContractDialog}
          contract={c}
          onClose={() => setShowSignContractDialog(false)}
          onSubmit={async (data) => {
            await signContract({ variables: { contractId, signingDate: data.signingDate } });
            setShowSignContractDialog(false);
          }}
        />
      )}
    </div>
  );
}
